'use client'

import { Badge, Cell, Container, Radio, Spacer, Text } from '@vinted/web-ui'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { BumpOptionModel } from '@marketplace-web/vas/vas-data'

type Props = {
  isSelected: boolean
  bumpOption: BumpOptionModel
  onSelect: () => void
  isBestValue?: boolean
  isLoading: boolean
}

const BumpOptionListItem = ({ onSelect, isSelected, bumpOption, isLoading }: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('bump_items.selection_confirmation_modal')
  const { id, description, isBestValue, countries } = bumpOption

  function handleSelect() {
    if (isSelected) return

    onSelect()
  }

  function renderBestValueBadge() {
    return (
      <>
        <Spacer size="small" orientation="horizontal" />
        <Badge
          theme="success"
          content={
            <Text
              as="span"
              theme="inverse"
              type="caption"
              text={translate('duration.best_value_badge_text')}
            />
          }
        />
        <Spacer size="small" orientation="horizontal" />
      </>
    )
  }

  function renderTitle() {
    return (
      <>
        {isBestValue && renderBestValueBadge()}
        <Text as="p" text={description} type="title" />
      </>
    )
  }

  function renderBody() {
    if (!countries?.length) return null

    return <Text as="span" type="caption" theme="muted" text={countries} />
  }

  function renderPriceAriaLabel() {
    if (isLoading) return ''

    return formatCurrencyAmount(bumpOption.discountedPrice, locale)
  }

  // Todo: radio name prop should be the same for all radio buttons after web-ui fixes id issue
  function renderSuffix() {
    return (
      <Container styling="narrow">
        <Radio
          checked={isSelected}
          name={`bump-option-${id}`}
          value={id}
          onChange={() => undefined}
          tabIndex={isSelected ? 0 : -1}
          aria={{
            'aria-label': `${description} ${renderPriceAriaLabel()}`,
          }}
        />
      </Container>
    )
  }

  return (
    <Cell
      clickable
      title={renderTitle()}
      body={renderBody()}
      suffix={renderSuffix()}
      styling="narrow"
      onClick={handleSelect}
      role="presentation"
      tabIndex={-1}
      testId={`bump-option-${id}`}
    />
  )
}

export default BumpOptionListItem
